/**
 * DataManagerAsset.ts
 *
 * Resolves asset dependencies from #category.id references in JSON data.
 * Replaces hardcoded GLOBAL_ATLAS with a declarative, data-driven system.
 *
 * Reference syntax: #category.id
 * Valid categories: projectiles, weapons, animations, entities, particles, sounds
 *
 * @see docs/c3_data_system.md - "Asset Reference System" section
 */

// ============================================================================
// Types
// ============================================================================

export interface ResolvedAssets {
  atlas: Set<string>;      // Texture keys to load as atlas (with .json)
  images: Set<string>;     // Texture keys to load as single images
  particles: Set<string>;  // Particle texture keys
  sounds: Set<string>;     // Sound keys
}

type CategoryType = 'projectiles' | 'weapons' | 'animations' | 'entities' | 'particles' | 'sounds';

interface GlobalsEntry {
  atlas?: string[];
  requires?: string[];
  _comment?: string;
}

interface GlobalsData {
  _meta?: { description: string; note: string };
  [key: string]: GlobalsEntry | { description: string; note: string } | undefined;
}

// ============================================================================
// Reference Parsing
// ============================================================================

const REFERENCE_REGEX = /^#(projectiles|weapons|animations|entities|particles|sounds)\.([a-z0-9_]+)$/;

/**
 * Parse a #category.id reference
 * @returns [category, id] or null if not a valid reference
 */
function parseReference(value: string): [CategoryType, string] | null {
  if (typeof value !== 'string' || !value.startsWith('#')) {
    return null;
  }
  const match = value.match(REFERENCE_REGEX);
  if (!match) {
    console.warn(`[DataManagerAsset] Invalid reference format: "${value}". Expected #category.id`);
    return null;
  }
  return [match[1] as CategoryType, match[2]];
}

// ============================================================================
// DataManagerAsset
// ============================================================================

const MAX_DEPTH = 100;

/**
 * Static manager for resolving asset dependencies from # references.
 */
export class DataManagerAsset {
  // Cached JSON data (loaded from scene cache)
  private static projectilesData: Record<string, unknown> | null = null;
  private static weaponsData: Record<string, unknown> | null = null;
  private static animationsData: Record<string, { texture: string }> | null = null;
  private static entitiesData: Record<string, unknown> | null = null;
  private static particlesData: Record<string, { texture?: string }> | null = null;
  private static soundsData: Record<string, unknown> | null = null;
  private static globalsData: GlobalsData | null = null;
  private static weaponsDefaultsData: {
    discovered?: string[];
    testingMode?: { discovered?: string[] };
  } | null = null;

  private static loaded = false;

  // JSON keys
  static readonly JSON_KEYS = {
    globals: 'globals',
    weaponsDefaults: 'weapons-defaults',
    projectilesC3: 'projectiles-c3data',
    projectilesOverrides: 'projectiles-overrides',
    weaponsC3: 'weapons-c3data',
    weaponsOverrides: 'weapons-overrides',
    animationsC3: 'animations-c3data',
    entitiesC3: 'entities-c3data',
    entitiesOverrides: 'entities-overrides',
    particlesC3: 'particles-c3data',
    particlesOverrides: 'particles-overrides',
    soundsC3: 'sounds-c3data',
    soundOverrides: 'sound-overrides',
  } as const;

  /**
   * Preload required JSON files (call in scene preload)
   */
  static preload(scene: Phaser.Scene): void {
    const load = (key: string, path: string) => {
      if (!scene.cache.json.exists(key)) {
        scene.load.json(key, path);
      }
    };

    load(this.JSON_KEYS.globals, '/assets/data/globals.json');
    load(this.JSON_KEYS.weaponsDefaults, '/assets/data/weapons-defaults.json');
    load(this.JSON_KEYS.projectilesC3, '/assets/data/projectiles-c3data.json');
    load(this.JSON_KEYS.projectilesOverrides, '/assets/data/projectiles-overrides.json');
    load(this.JSON_KEYS.weaponsC3, '/assets/data/weapons-c3data.json');
    load(this.JSON_KEYS.weaponsOverrides, '/assets/data/weapons-overrides.json');
    load(this.JSON_KEYS.animationsC3, '/assets/data/animations-c3data.json');
    load(this.JSON_KEYS.entitiesC3, '/assets/data/entities-c3data.json');
    load(this.JSON_KEYS.entitiesOverrides, '/assets/data/entities-overrides.json');
    load(this.JSON_KEYS.particlesC3, '/assets/data/particles-c3data.json');
    load(this.JSON_KEYS.particlesOverrides, '/assets/data/particles-overrides.json');
    load(this.JSON_KEYS.soundsC3, '/assets/data/sounds-c3data.json');
    load(this.JSON_KEYS.soundOverrides, '/assets/data/sound-overrides.json');
  }

  /**
   * Load and cache JSON data from scene cache (call in scene create)
   */
  static load(scene: Phaser.Scene): void {
    if (this.loaded) return;

    // Load globals
    this.globalsData = scene.cache.json.get(this.JSON_KEYS.globals) as GlobalsData | null;
    if (!this.globalsData) {
      console.error('[DataManagerAsset] globals.json not loaded');
    }

    // Load weapons-defaults
    this.weaponsDefaultsData = scene.cache.json.get(this.JSON_KEYS.weaponsDefaults);
    if (!this.weaponsDefaultsData) {
      console.error('[DataManagerAsset] weapons-defaults.json not loaded');
    }

    // Merge c3data + overrides for each category
    this.projectilesData = this.mergeData(
      scene.cache.json.get(this.JSON_KEYS.projectilesC3)?.projectiles,
      scene.cache.json.get(this.JSON_KEYS.projectilesOverrides)?.projectiles
    );

    // Weapons: merge guns, bombs, tractors
    const weaponsC3 = scene.cache.json.get(this.JSON_KEYS.weaponsC3);
    const weaponsOverrides = scene.cache.json.get(this.JSON_KEYS.weaponsOverrides);
    this.weaponsData = {
      ...this.mergeData(weaponsC3?.guns, weaponsOverrides?.guns),
      ...this.mergeData(weaponsC3?.bombs, weaponsOverrides?.bombs),
      ...this.mergeData(weaponsC3?.tractors, weaponsOverrides?.tractors),
    };

    // Animations: c3data only (no overrides for animations themselves)
    const animC3 = scene.cache.json.get(this.JSON_KEYS.animationsC3);
    this.animationsData = animC3?.entities ?? {};

    // Entities: merge c3data + overrides
    const entitiesC3 = scene.cache.json.get(this.JSON_KEYS.entitiesC3);
    const entitiesOverrides = scene.cache.json.get(this.JSON_KEYS.entitiesOverrides);
    this.entitiesData = this.mergeData(entitiesC3?.entities, entitiesOverrides?.entities);

    // Particles: merge c3data + overrides
    const particlesC3 = scene.cache.json.get(this.JSON_KEYS.particlesC3);
    const particlesOverrides = scene.cache.json.get(this.JSON_KEYS.particlesOverrides);
    this.particlesData = this.mergeData(particlesC3?.particles, particlesOverrides?.particles);

    // Sounds: merge c3data + overrides
    const soundsC3 = scene.cache.json.get(this.JSON_KEYS.soundsC3);
    const soundOverrides = scene.cache.json.get(this.JSON_KEYS.soundOverrides);
    this.soundsData = this.mergeData(soundsC3?.sounds, soundOverrides?.sounds);

    this.loaded = true;
    // console.log('[DataManagerAsset] Data loaded and ready for resolution');
  }

  /**
   * Merge c3data with overrides (shallow merge per entity)
   */
  private static mergeData(
    base: Record<string, unknown> | undefined,
    overrides: Record<string, unknown> | undefined
  ): Record<string, unknown> {
    const result: Record<string, unknown> = {};

    // Add base data
    if (base) {
      for (const [key, value] of Object.entries(base)) {
        result[key.toLowerCase()] = value;
      }
    }

    // Merge overrides
    if (overrides) {
      for (const [key, value] of Object.entries(overrides)) {
        const lowerKey = key.toLowerCase();
        if (result[lowerKey] && typeof result[lowerKey] === 'object' && typeof value === 'object') {
          result[lowerKey] = { ...(result[lowerKey] as object), ...(value as object) };
        } else {
          result[lowerKey] = value;
        }
      }
    }

    return result;
  }

  /**
   * Resolve all global assets (from globals.json)
   * @returns Set of all assets needed for global preload
   */
  static resolveGlobals(): ResolvedAssets {
    if (!this.loaded) {
      console.error('[DataManagerAsset] Not loaded. Call load() first.');
      return { atlas: new Set(), images: new Set(), particles: new Set(), sounds: new Set() };
    }

    const result: ResolvedAssets = {
      atlas: new Set(),
      images: new Set(),
      particles: new Set(),
      sounds: new Set(),
    };

    if (!this.globalsData) return result;

    // Process each entry in globals.json
    for (const [key, entry] of Object.entries(this.globalsData)) {
      if (key === '_meta' || !entry || typeof entry !== 'object') continue;

      const globalEntry = entry as GlobalsEntry;

      // Direct atlas entries
      if (globalEntry.atlas) {
        for (const atlasKey of globalEntry.atlas) {
          result.atlas.add(atlasKey.toLowerCase());
        }
      }

      // Resolve requires references
      if (globalEntry.requires) {
        for (const ref of globalEntry.requires) {
          this.resolveReference(ref, result, new Set(), 0);
        }
      }
    }

    // Auto-resolve all weapons from weapons-defaults (normal + testingMode)
    if (this.weaponsDefaultsData) {
      const allWeaponIds = new Set<string>();

      // Normal discovered weapons
      if (this.weaponsDefaultsData.discovered) {
        for (const id of this.weaponsDefaultsData.discovered) {
          allWeaponIds.add(id.toLowerCase());
        }
      }

      // Testing mode weapons (always load for dev)
      if (this.weaponsDefaultsData.testingMode?.discovered) {
        for (const id of this.weaponsDefaultsData.testingMode.discovered) {
          allWeaponIds.add(id.toLowerCase());
        }
      }

      // Resolve each weapon → projectile → texture
      for (const weaponId of allWeaponIds) {
        this.resolveReference(`#weapons.${weaponId}`, result, new Set(), 0);
      }
    }

    // console.log(`[DataManagerAsset] Resolved globals: ${result.atlas.size} atlas, ${result.sounds.size} sounds`);
    return result;
  }

  /**
   * Resolve assets for a list of entity IDs (for level preloading)
   * @param entityIds List of entity IDs in the level
   * @returns Set of all assets needed for those entities
   */
  static resolveEntities(entityIds: string[]): ResolvedAssets {
    if (!this.loaded) {
      console.error('[DataManagerAsset] Not loaded. Call load() first.');
      return { atlas: new Set(), images: new Set(), particles: new Set(), sounds: new Set() };
    }

    const result: ResolvedAssets = {
      atlas: new Set(),
      images: new Set(),
      particles: new Set(),
      sounds: new Set(),
    };

    for (const id of entityIds) {
      this.resolveReference(`#entities.${id.toLowerCase()}`, result, new Set(), 0);
    }

    return result;
  }

  /**
   * Resolve a single # reference recursively
   */
  private static resolveReference(
    reference: string,
    result: ResolvedAssets,
    visited: Set<string>,
    depth: number
  ): void {
    // Cycle detection
    if (depth > MAX_DEPTH) {
      console.error(`[DataManagerAsset] CYCLE DETECTED at depth ${depth}`);
      console.error('[DataManagerAsset] Resolution chain:');
      console.table([...visited].map((ref, i) => ({ order: i, reference: ref })));
      throw new Error('Asset reference cycle detected');
    }

    // Parse reference
    const parsed = parseReference(reference);
    if (!parsed) return;

    const [category, id] = parsed;
    const refKey = `${category}.${id}`;

    // Skip if already visited
    if (visited.has(refKey)) return;
    visited.add(refKey);

    // Get data for this category
    const data = this.getDataForCategory(category, id);
    if (!data) {
      console.warn(`[DataManagerAsset] Reference not found: ${reference}`);
      return;
    }

    // Process based on category
    switch (category) {
      case 'animations':
        this.processAnimationRef(id, data, result);
        break;
      case 'projectiles':
        this.processProjectileRef(id, data, result, visited, depth);
        break;
      case 'weapons':
        this.processWeaponRef(id, data, result, visited, depth);
        break;
      case 'entities':
        this.processEntityRef(id, data, result, visited, depth);
        break;
      case 'particles':
        this.processParticleRef(id, data, result);
        break;
      case 'sounds':
        this.processSoundRef(id, result);
        break;
    }
  }

  /**
   * Get data for a category/id
   */
  private static getDataForCategory(category: CategoryType, id: string): unknown {
    switch (category) {
      case 'projectiles':
        return this.projectilesData?.[id];
      case 'weapons':
        return this.weaponsData?.[id];
      case 'animations':
        return this.animationsData?.[id];
      case 'entities':
        return this.entitiesData?.[id];
      case 'particles':
        return this.particlesData?.[id];
      case 'sounds':
        return this.soundsData?.[id];
      default:
        return null;
    }
  }

  /**
   * Process animation reference - adds texture to atlas
   */
  private static processAnimationRef(
    id: string,
    data: unknown,
    result: ResolvedAssets
  ): void {
    const animData = data as { texture?: string };
    // Animation texture = atlas key (usually same as id)
    const textureKey = (animData?.texture ?? id).toLowerCase();
    result.atlas.add(textureKey);
  }

  /**
   * Process projectile reference - resolves texture and effects
   */
  private static processProjectileRef(
    id: string,
    data: unknown,
    result: ResolvedAssets,
    visited: Set<string>,
    depth: number
  ): void {
    const projData = data as {
      texture?: string;
      effects?: {
        onHit?: {
          sprite?: string;
          particles?: string;
        };
      };
      spawnSound?: string;
      hitSound?: string;
    };

    // Add projectile texture as atlas
    const textureKey = (projData.texture ?? id).toLowerCase();
    result.atlas.add(textureKey);

    // Resolve effect references
    if (projData.effects?.onHit?.sprite) {
      this.resolveReference(projData.effects.onHit.sprite, result, visited, depth + 1);
    }
    if (projData.effects?.onHit?.particles) {
      this.resolveReference(projData.effects.onHit.particles, result, visited, depth + 1);
    }

    // Resolve sound references
    if (projData.spawnSound && projData.spawnSound.startsWith('#')) {
      this.resolveReference(projData.spawnSound, result, visited, depth + 1);
    }
    if (projData.hitSound && projData.hitSound.startsWith('#')) {
      this.resolveReference(projData.hitSound, result, visited, depth + 1);
    }
  }

  /**
   * Process weapon reference - resolves projectile and sounds
   */
  private static processWeaponRef(
    id: string,
    data: unknown,
    result: ResolvedAssets,
    visited: Set<string>,
    depth: number
  ): void {
    const weaponData = data as {
      projectileType?: string;
      sound?: string;
      texture?: string;
    };

    // If weapon has its own texture (like tractorbeam), add it
    if (weaponData.texture) {
      // Tractor beams are loaded as individual images, not atlas
      // For now, mark as atlas and let Preloader handle the special case
      result.atlas.add(id.toLowerCase());
    }

    // Resolve projectile reference
    if (weaponData.projectileType) {
      this.resolveReference(weaponData.projectileType, result, visited, depth + 1);
    }

    // Resolve sound reference
    if (weaponData.sound && weaponData.sound.startsWith('#')) {
      this.resolveReference(weaponData.sound, result, visited, depth + 1);
    }
  }

  /**
   * Process entity reference - resolves texture and behaviors
   */
  private static processEntityRef(
    id: string,
    data: unknown,
    result: ResolvedAssets,
    visited: Set<string>,
    depth: number
  ): void {
    const entityData = data as {
      texture?: string;
      behaviors?: {
        shooter?: { projectileType?: string };
        transform?: { spawnOnTransform?: Array<string | { entityId: string }> };
      };
    };

    // Add entity texture as atlas
    const textureKey = (entityData.texture ?? id).toLowerCase();
    result.atlas.add(textureKey);

    // Resolve shooter projectile
    if (entityData.behaviors?.shooter?.projectileType) {
      this.resolveReference(
        entityData.behaviors.shooter.projectileType,
        result,
        visited,
        depth + 1
      );
    }

    // Resolve transform spawns
    if (entityData.behaviors?.transform?.spawnOnTransform) {
      for (const spawn of entityData.behaviors.transform.spawnOnTransform) {
        const entityId = typeof spawn === 'string' ? spawn : spawn.entityId;
        this.resolveReference(`#entities.${entityId.toLowerCase()}`, result, visited, depth + 1);
      }
    }
  }

  /**
   * Process particle reference - adds particle texture
   */
  private static processParticleRef(
    id: string,
    data: unknown,
    result: ResolvedAssets
  ): void {
    const particleData = data as { texture?: string };
    const textureKey = (particleData?.texture ?? id).toLowerCase();
    result.particles.add(textureKey);
  }

  /**
   * Process sound reference - adds sound key
   */
  private static processSoundRef(id: string, result: ResolvedAssets): void {
    result.sounds.add(id.toLowerCase());
  }

  /**
   * Check if manager has been loaded
   */
  static isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Reset manager (for testing or scene restarts)
   */
  static reset(): void {
    this.projectilesData = null;
    this.weaponsData = null;
    this.animationsData = null;
    this.entitiesData = null;
    this.particlesData = null;
    this.soundsData = null;
    this.globalsData = null;
    this.weaponsDefaultsData = null;
    this.loaded = false;
  }
}
